import { formatWhen } from "@/components/boo/access-copy";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { HostIcon } from "@/components/HostIcon";
import { InlineMarkdown } from "@/components/InlineMarkdown";
import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  api,
  apiErrorCode,
  type ConnectorActionDecl,
  type ConnectorCodeHostApproval,
  type ConnectorDetail,
  type ConnectorReview,
  type ConnectorVariableDecl,
} from "@/lib/api";
import { hostLabel } from "@/lib/code-host";
import {
  STATE_LABEL,
  VERIFY_ACTION,
  buildCheckLabel,
  connectorKeys,
  invalidateConnectors,
  defaultStateFor,
  requestNoun,
  requestNumber,
  shortCommit,
} from "@/lib/connectors";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Clock, ExternalLink, GitMerge, KeyRound, Loader2, RefreshCw, Send, ShieldCheck, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ConnectorDiff } from "./ConnectorDiff";
import { ChecksList, RiskBadge, SecretBadge } from "./parts";

type Change = "added" | "changed" | "removed" | "same";

const isCodeHost = (a: ConnectorReview["approval"]): a is ConnectorCodeHostApproval =>
  !!a && "prUrl" in a;

// One commit waiting for approval, against the running version: what the
// platform's build found, the actions in plain words, the variables, and the
// code. Boo-hosted: comments go back to Boo and Approve starts it running.
// Code host: people review in the pull request; merging to main puts it live.
export function ReviewPanel({
  connector,
  containerId,
  commit,
}: {
  connector: ConnectorDetail;
  containerId: string;
  commit: string;
}) {
  const query = useQuery({
    queryKey: connectorKeys.review(containerId, connector.slug, commit),
    queryFn: () => api.connectors.review(containerId, connector.slug, commit),
    refetchInterval: (q) => (q.state.data?.status === "checking" ? 5_000 : false),
  });
  // Checking just ended: the header badge and tabs come from the detail.
  const queryClient = useQueryClient();
  const status = query.data?.status;
  const wasChecking = useRef(false);
  useEffect(() => {
    if (wasChecking.current && status !== "checking") {
      void queryClient.invalidateQueries({ queryKey: connectorKeys.detail(containerId, connector.slug), exact: true });
      void queryClient.invalidateQueries({ queryKey: connectorKeys.list(containerId), exact: true });
    }
    wasChecking.current = status === "checking";
  }, [status, queryClient, containerId, connector.slug]);
  if (query.isLoading) return <LoadingIndicator />;
  if (query.isError) return <InlineError error={query.error} />;
  return <ReviewBody connector={connector} containerId={containerId} review={query.data!} />;
}

function ReviewBody({
  connector,
  containerId,
  review,
}: {
  connector: ConnectorDetail;
  containerId: string;
  review: ConnectorReview;
}) {
  const base = connector.running?.commit ?? null;
  const codeHost = isCodeHost(review.approval) ? review.approval : null;
  const secretFiles = review.files.filter((f) => f.status !== "unchanged" && f.touchesSecret).length;
  const variables = variableRows(connector, review);
  const until = codeHost ? `merged into ${connector.main?.branch ?? "main"}` : "approved";

  return (
    <div className="flex flex-col gap-8">
      <section className="flex flex-col gap-1 rounded-lg border bg-muted/30 p-4">
        <p className="text-sm font-medium">
          Version <code className="font-mono">{shortCommit(review.commit)}</code>
          {review.status === "checking" && <span className="ml-2 text-muted-foreground">Still being checked…</span>}
        </p>
        <p className="text-xs text-muted-foreground text-pretty">
          {base === null ? (
            `This is the first version. Nothing runs until it’s ${until}.`
          ) : (
            <>
              Compared with the running version <code className="font-mono">{shortCommit(base)}</code>, which keeps running until this one is {until}.
            </>
          )}
        </p>
      </section>

      {codeHost && <CodeHostCard connector={connector} containerId={containerId} review={review} approval={codeHost} />}

      <BuildChecks review={review} />

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold">What Boo can do with it</h2>
        <ActionList connector={connector} review={review} firstVersion={base === null} />
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold">What it needs from you</h2>
        {variables.length === 0 ? (
          <p className="rounded-lg border p-3 text-sm text-muted-foreground">Nothing. It needs no values to connect.</p>
        ) : (
          <div className="divide-y rounded-lg border">
            {variables.map(({ v, change }) => (
              <div key={v.name} className={cn("flex items-center gap-3 p-3", change === "removed" && "opacity-60")}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium leading-tight">{v.label}</p>
                  <code className="font-mono text-xs text-muted-foreground">{v.name}</code>
                </div>
                {v.secret && <SecretBadge />}
                <ChangeBadge change={change} />
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Secret values stay with the connection. Boo never sees them.
        </p>
      </section>

      {!codeHost && (
        <section className="flex flex-col gap-2">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <h2 className="text-sm font-semibold">Code changes</h2>
            {secretFiles > 0 && (
              <p className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
                <KeyRound className="size-3" />
                {secretFiles === 1 ? "1 file changes" : `${secretFiles} files change`} how a secret is used. Lines that read it directly are marked.
              </p>
            )}
          </div>
          <ConnectorDiff
            files={review.files}
            before={review.baseVariables ?? connector.variables}
            after={review.versionVariables ?? variables.map((r) => r.v)}
          />
        </section>
      )}

      {!codeHost && <Comments connector={connector} containerId={containerId} review={review} />}
      {!codeHost && <ApproveBar connector={connector} containerId={containerId} review={review} />}
    </div>
  );
}

// The platform's build of this commit. Folded to one line when all passed.
function BuildChecks({ review }: { review: ConnectorReview }) {
  const allOk = review.status !== "checking" && review.checks.length > 0 && review.checks.every((c) => c.ok);
  const [open, setOpen] = useState(!allOk);
  const checks = review.checks.map((c) => ({ ...c, name: buildCheckLabel(c.name) }));
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">Platform checks</h2>
        {allOk && (
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setOpen((o) => !o)}>
            {open ? "Hide" : "Show all"}
          </Button>
        )}
      </div>
      {open ? (
        <ChecksList checks={checks} pending={review.status === "checking"} />
      ) : (
        <p className="flex items-center gap-2 rounded-lg border p-3 text-sm">
          <CheckCircle2 className="size-4 text-emerald-600" />
          All {checks.length} checks passed: it builds, its code matches connector.yaml, and its tests pass.
        </p>
      )}
    </section>
  );
}

// Every variable this version declares plus the ones it drops, marked.
function variableRows(connector: ConnectorDetail, review: ConnectorReview) {
  const rows: { v: ConnectorVariableDecl; change: Change }[] = [];
  const changed = new Map(review.variables.changed.map((c) => [c.name, c.after]));
  const touched = new Set([
    ...review.variables.added.map((v) => v.name),
    ...review.variables.removed.map((v) => v.name),
    ...changed.keys(),
  ]);
  for (const v of connector.running ? connector.variables : []) {
    if (touched.has(v.name)) continue;
    rows.push({ v, change: "same" });
  }
  for (const [, v] of changed) rows.push({ v, change: "changed" });
  for (const v of review.variables.added) rows.push({ v, change: "added" });
  for (const v of review.variables.removed) rows.push({ v, change: "removed" });
  return rows;
}

function ChangeBadge({ change }: { change: Change }) {
  if (change === "same") return null;
  return (
    <Badge
      variant="secondary"
      className={cn(
        change === "added" && "bg-emerald-500/15 text-emerald-800 dark:text-emerald-300",
        change === "changed" && "bg-sky-500/15 text-sky-800 dark:text-sky-300",
        change === "removed" && "bg-red-500/15 text-red-800 dark:text-red-300",
      )}
    >
      {change === "added" ? "New" : change === "changed" ? "Changed" : "Removed"}
    </Badge>
  );
}

type ActionRow = { a: ConnectorActionDecl; change: Change; before?: ConnectorActionDecl };

function ActionList({
  connector,
  review,
  firstVersion,
}: {
  connector: ConnectorDetail;
  review: ConnectorReview;
  firstVersion: boolean;
}) {
  const changed = new Map(review.actions.changed.map((c) => [c.name, c]));
  const touched = new Set([
    ...review.actions.added.map((a) => a.name),
    ...review.actions.removed.map((a) => a.name),
    ...changed.keys(),
  ]);
  const rows: ActionRow[] = [];
  for (const a of firstVersion ? [] : connector.actions) {
    if (a.name === VERIFY_ACTION) continue;
    const c = changed.get(a.name);
    if (c) rows.push({ a: c.after, change: "changed", before: c.before });
    else if (!touched.has(a.name)) rows.push({ a, change: "same" });
  }
  for (const c of changed.values()) if (!rows.some((r) => r.a.name === c.name)) rows.push({ a: c.after, change: "changed", before: c.before });
  for (const a of review.actions.added) if (a.name !== VERIFY_ACTION) rows.push({ a, change: "added" });
  for (const a of review.actions.removed) rows.push({ a, change: "removed" });

  return (
    <div className="divide-y rounded-lg border">
      <div className="flex flex-col gap-1 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <code className="font-mono text-sm font-medium">{VERIFY_ACTION}</code>
          <RiskBadge risk="read" />
          <Badge variant="outline" className="gap-1 border-primary/40">
            <ShieldCheck className="size-3" />
            {review.verify.note || "Runs when the connection is saved"}
          </Badge>
        </div>
        <InlineMarkdown text={review.verify.description} className="block text-sm text-muted-foreground text-pretty" />
      </div>
      {rows.map(({ a, change, before }) => {
        // What it starts as once approved.
        const startsAs = firstVersion ? defaultStateFor(a.risk) : change === "added" ? "off" : null;
        return (
          <div key={a.name} className={cn("flex flex-col gap-1 p-3", change === "removed" && "opacity-60")}>
            <div className="flex flex-wrap items-center gap-2">
              <code className="font-mono text-sm font-medium">{a.name}</code>
              <RiskBadge risk={a.risk} />
              <ChangeBadge change={change} />
              {startsAs && change !== "removed" && (
                <span className="ml-auto text-xs text-muted-foreground">Starts {STATE_LABEL[startsAs].toLowerCase()}</span>
              )}
            </div>
            <InlineMarkdown text={a.description} className="block text-sm text-muted-foreground text-pretty" />
            {before && <WasLine before={before} after={a} />}
          </div>
        );
      })}
    </div>
  );
}

function WasLine({ before, after }: { before: ConnectorActionDecl; after: ConnectorActionDecl }) {
  const parts: React.ReactNode[] = [];
  if (before.risk !== after.risk) parts.push(<RiskBadge key="r" risk={before.risk} className="h-5" />);
  if (before.description !== after.description) parts.push(<span key="d" className="line-through">{before.description}</span>);
  if (parts.length === 0) return null;
  return <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">Was: {parts}</p>;
}

function CodeHostCard({
  connector,
  containerId,
  review,
  approval,
}: {
  connector: ConnectorDetail;
  containerId: string;
  review: ConnectorReview;
  approval: ConnectorCodeHostApproval;
}) {
  const queryClient = useQueryClient();
  const kind = connector.source.kind === "gitlab" ? "gitlab" : "github";
  const host = hostLabel(kind);
  const request = requestNoun(kind);
  const ref = `${request}${approval.prNumber ? ` ${requestNumber(kind, approval.prNumber)}` : ""}`;
  const branch = connector.main?.branch ?? "main";
  const phase = codeHostPhase(review, approval);
  // The detail GET says why the pending request isn't live, in the backend's words.
  const pendingApproval = connector.pending?.commit === review.commit ? connector.pending.approval : null;
  const pendingReason = pendingApproval && pendingApproval.kind !== "dashboard" ? pendingApproval.reason : null;
  const recheck = useMutation({
    mutationFn: () => api.connectors.checkHost(containerId, connector.slug, review.commit),
    onSuccess: (res) => {
      toast.success(`${connector.name} now runs ${shortCommit(res.runningCommit ?? review.commit)} from ${res.main?.branch ?? branch}`);
      invalidateConnectors(queryClient, containerId);
    },
    onError: (err) => {
      const code = apiErrorCode(err);
      if (code && NOT_LIVE_YET.has(code)) {
        toast.info(err instanceof Error && err.message ? err.message : `Nothing new runs from ${host} yet`);
        invalidateConnectors(queryClient, containerId);
        return;
      }
      toast.error(err instanceof Error ? err.message : `Could not check ${host}`);
    },
  });
  const runsWhatsOnMain = `${connector.name} runs what’s on ${branch}; it goes live when someone merges the ${request} and its checks pass.`;
  const status: Record<CodeHostPhase, string> = {
    live: `This version runs. It’s what’s on ${branch}.`,
    none: `No ${request} on ${host} has this commit as its head yet. Ask Boo to open one. ${runsWhatsOnMain}`,
    closed: `The ${ref} was closed without merging. Nothing new runs.`,
    newer: `The ${ref} has newer commits than this version. Boo registers the newest, and it shows up here.`,
    merged: pendingReason || `The ${ref} was merged. Check ${host} to see what runs now.`,
    open: `Waiting for someone to merge the ${ref} on ${host}. ${runsWhatsOnMain}`,
  };
  const done = phase === "live";
  const stuck = phase === "closed" || phase === "newer";
  return (
    <section className="flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-center gap-2">
        <HostIcon host={kind} className="size-4" />
        <p className="min-w-0 flex-1 text-sm font-medium">
          {phase === "none" ? `Kept on ${host}` : `Reviewed in ${ref} on ${host}`}
        </p>
        {approval.prUrl && (
          <Button asChild size="sm" variant="outline" className="gap-1.5">
            <a href={approval.prUrl} target="_blank" rel="noreferrer">
              Open on {host}
              <ExternalLink className="size-3.5" />
            </a>
          </Button>
        )}
      </div>
      <div className="flex items-start gap-2 text-sm">
        {done ? (
          <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />
        ) : stuck ? (
          <XCircle className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        ) : phase === "merged" ? (
          <GitMerge className="mt-0.5 size-4 shrink-0 text-amber-600" />
        ) : (
          <Clock className="mt-0.5 size-4 shrink-0 text-amber-600" />
        )}
        <p className="text-muted-foreground text-pretty">{status[phase]}</p>
      </div>
      {approval.approvedBy.length > 0 && phase === "open" && (
        <p className="text-xs text-muted-foreground">
          Approved on {host} by {approval.approvedBy.join(", ")}. Merging is what puts it live.
        </p>
      )}
      {connector._permissions.canConfigure && !done && !stuck && (
        <div>
          <Button size="sm" variant="outline" className="gap-1.5" disabled={recheck.isPending} onClick={() => recheck.mutate()}>
            {recheck.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
            Check on {host}
          </Button>
        </div>
      )}
    </section>
  );
}

// Where a code-host version's request stands. On GitHub and GitLab the
// default branch is what runs, so a merge is what puts it live.
type CodeHostPhase = "live" | "none" | "closed" | "newer" | "merged" | "open";

function codeHostPhase(review: ConnectorReview, a: ConnectorCodeHostApproval): CodeHostPhase {
  if (review.status === "approved") return "live";
  if (a.state === "none") return "none";
  if (a.state === "closed") return "closed";
  if (a.state === "merged") return "merged";
  return a.headMatches ? "open" : "newer";
}

// 409s from Check on GitLab/GitHub that mean "not yet", not "something broke".
const NOT_LIVE_YET = new Set<string>([
  "not_merged",
  "closed",
  "boo_moved_main",
  "mover_unknown",
  "checks_failed",
  "no_connector",
  "not_ready",
]);

function Comments({
  connector,
  containerId,
  review,
}: {
  connector: ConnectorDetail;
  containerId: string;
  review: ConnectorReview;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const send = useMutation({
    mutationFn: () => api.connectors.comment(containerId, connector.slug, review.commit, { body: draft.trim() }),
    onSuccess: () => {
      setDraft("");
      toast.success("Sent to Boo");
      queryClient.invalidateQueries({ queryKey: connectorKeys.review(containerId, connector.slug, review.commit) });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not send"),
  });
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold">Comments</h2>
      {review.comments.length > 0 && (
        <ul className="flex flex-col gap-2">
          {review.comments.map((c) => {
            const fromBoo = !c.author.userId;
            return (
              <li key={c.id} className={cn("rounded-lg border p-3", fromBoo ? "bg-muted/40" : "bg-background")}>
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{c.author.name}</span> · {formatWhen(c.at)}
                  {c.path && (
                    <>
                      {" "}
                      on{" "}
                      <code className="font-mono">
                        {c.path}
                        {c.line ? `:${c.line}` : ""}
                      </code>
                    </>
                  )}
                </p>
                <InlineMarkdown text={c.body} className="mt-1 block text-sm text-pretty" />
              </li>
            );
          })}
        </ul>
      )}
      {connector._permissions.canConfigure && (
        <>
          <Textarea
            aria-label="Comment to Boo"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Ask Boo for a change. It answers here and registers a new version."
            className="min-h-20"
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={!draft.trim() || send.isPending}
              onClick={() => send.mutate()}
            >
              <Send className="size-3.5" />
              {send.isPending ? "Sending…" : "Send to Boo"}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}

function ApproveBar({
  connector,
  containerId,
  review,
}: {
  connector: ConnectorDetail;
  containerId: string;
  review: ConnectorReview;
}) {
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const approve = useMutation({
    mutationFn: () => api.connectors.approve(containerId, connector.slug, review.commit),
    onSuccess: () => {
      setConfirm(false);
      toast.success(`Approved. ${connector.name} now runs version ${shortCommit(review.commit)}`);
      invalidateConnectors(queryClient, containerId);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not approve"),
  });

  const approval = review.approval && !isCodeHost(review.approval) ? review.approval : null;
  if (approval?.approvedBy) {
    return (
      <p className="flex items-center gap-2 rounded-lg border p-3 text-sm text-muted-foreground">
        <CheckCircle2 className="size-4 text-emerald-600" />
        Approved by {approval.approvedBy.name}
        {approval.approvedAt ? `, ${formatWhen(approval.approvedAt)}` : ""}.
      </p>
    );
  }

  const checksOk = review.checks.length > 0 && review.checks.every((c) => c.ok);
  const blocked = !connector._permissions.canConfigure
    ? "Only this Boo’s admins can approve."
    : review.status === "checking"
      ? "The platform is still checking this version."
      : review.status === "superseded"
        ? "Boo registered a newer version. Review that one instead."
        : !checksOk || review.status === "failed"
          ? "The platform checks failed, so this version can’t be approved. Ask Boo to fix it."
          : !review.canApprove
            ? "This version can’t be approved."
            : null;
  const added = review.actions.added.filter((a) => a.name !== VERIFY_ACTION).length;

  return (
    <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t bg-background/95 py-3 backdrop-blur supports-backdrop-filter:bg-background/80">
      <p className="text-xs text-muted-foreground text-pretty">
        {blocked ?? `Approving covers exactly version ${shortCommit(review.commit)}. Any later change needs a new approval.`}
      </p>
      <Button size="sm" className="shrink-0" disabled={blocked !== null} onClick={() => setConfirm(true)}>
        Approve
      </Button>
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        variant="default"
        title={`Approve version ${shortCommit(review.commit)}?`}
        description={
          connector.running === null
            ? `${connector.name} starts running with this code. Actions that read start on, actions that change data ask first, and actions that delete start off.`
            : `${connector.name} switches to this code.${added > 0 ? ` ${added === 1 ? "The new action starts" : `The ${added} new actions start`} off until you turn ${added === 1 ? "it" : "them"} on.` : ""}`
        }
        confirmLabel="Approve"
        loading={approve.isPending}
        onConfirm={() => approve.mutate()}
      />
    </div>
  );
}
